import { useMemo } from 'react';
import { Tag, Typography } from 'antd';
import { SmartDataTableProps } from '@src/components/SmartDataTable/types';
import { useStatusRender } from './useStatusRender';
import { useUserTimestampRender } from '@src/hooks';
import { STATUS_FILTER_OPTIONS } from '../constants';
import dayjs from 'dayjs';

const { Text } = Typography;

export const useSuppliersColumns = (): SmartDataTableProps['columns'] => {
  const getStatusConfig = useStatusRender();
  const userTimestampRender = useUserTimestampRender({
    size: 'small',
  });

  return useMemo(
    () => [
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id',
        sorter: true,
        minWidth: 80,
        ellipsis: true,
        render: (value: any) => (
          <Text
            style={{
              fontSize: '12px',
              color: '#8c8c8c',
              backgroundColor: '#f5f5f5',
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            #{value}
          </Text>
        ),
      },
      {
        key: 'supplier_info',
        title: 'Supplier Info',
        dataIndex: 'name',
        sorter: false,
        minWidth: 200,
        render: (name: any, record: any) => (
          <div>
            <div style={{ fontWeight: 500, color: '#262626' }}>
              {record.name || 'N/A'}
            </div>
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
              {record.email || 'No email'}
            </div>
          </div>
        ),
      },
      {
        key: 'phone',
        title: 'Phone',
        dataIndex: 'phone',
        sorter: false,
        minWidth: 130,
        render: (phone: string) => (
          <Text style={{ color: '#52c41a' }}>+{phone || 'N/A'}</Text>
        ),
      },
      {
        key: 'status',
        title: 'Status',
        dataIndex: 'status',
        sorter: true,
        minWidth: 100,
        filterable: true,
        filterType: 'multiselect',
        filterOptions: [...STATUS_FILTER_OPTIONS],
        render: (status: string) => getStatusConfig(status),
      },
      {
        key: 'total_orders',
        title: 'Total Orders',
        dataIndex: 'total_orders',
        sorter: true,
        minWidth: 120,
        render: (orders: number) => (
          <Tag color="blue" style={{ fontWeight: 500 }}>
            {orders || 0}
          </Tag>
        ),
      },
      {
        key: 'total_amount',
        title: 'Total Amount',
        dataIndex: 'total_amount',
        sorter: true,
        minWidth: 130,
        render: (amount: number) => (
          <Text style={{ fontWeight: 500, color: '#52c41a' }}>
            ₹
            {amount
              ? Number(amount).toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                })
              : '0.00'}
          </Text>
        ),
      },
      {
        key: 'last_order_date',
        title: 'Last Order',
        dataIndex: 'last_order_date',
        sorter: true,
        minWidth: 120,
        render: (date: string) => (
          <div>
            {date ? (
              <div style={{ fontSize: '13px', color: '#262626' }}>
                {dayjs(date).format('DD MMM YYYY')}
              </div>
            ) : (
              <Text style={{ color: '#8c8c8c' }}>No orders</Text>
            )}
          </div>
        ),
      },
      {
        key: 'user_timestamp',
        title: 'User & Timestamp',
        dataIndex: 'user_info',
        sorter: false,
        minWidth: 200,
        render: (value: any, record: any) => userTimestampRender(record),
      },
    ],
    [getStatusConfig, userTimestampRender]
  );
};
